import { InfoCircleOutlined } from '@ant-design/icons';
import { Space, Table, Tooltip } from 'antd';
import { pick } from 'lodash/fp';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as permissionKind from '../../../../../shared/permissions';
import { type InventoryDetailsDataFragment } from '../../../../api/fragments/InventoryDetailsData';
import { type InventoryListDataFragment } from '../../../../api/fragments/InventoryListData';
import { type StockInventorySpecsFragment } from '../../../../api/fragments/StockInventorySpecs';
import { useListInventoriesQuery } from '../../../../api/queries/listInventories';
import { useAccount } from '../../../../components/contexts/AccountContextManager';
import { useCompany } from '../../../../components/contexts/CompanyContextManager';
import { useMultipleDealerIds } from '../../../../components/contexts/DealerContextManager';
import PaginatedTableWithContext from '../../../../components/PaginatedTableWithContext';
import InternalErrorResult from '../../../../components/results/InternalErrorResult';
import NotFoundResult from '../../../../components/results/NotFoundResult';
import useSearchInDropDown from '../../../../components/useSearchInDropDown';
import hasPermissions from '../../../../utilities/hasPermissions';
import renderBooleanIcon from '../../../../utilities/renderBooleanIcon';
import renderSearchIcon from '../../../../utilities/renderSearchIcon';
import useSortAndFilterCache, { SortAndFilterCacheKey } from '../../../../utilities/useSortAndFilterCache';
import useTranslatedString from '../../../../utilities/useTranslatedString';
import { type HeaderTabs } from '../ModelConfiguratorDetailsInnerPage';
import useListReducer from './useListReducer';
import useSortOrder, { getSortField } from './useSortOrder';

export type InventoryConfiguratorListProps = {
    modelConfiguratorId: string;
    activeTab: HeaderTabs;
};

const InventoryConfiguratorList = ({ modelConfiguratorId, activeTab }: InventoryConfiguratorListProps) => {
    const { t } = useTranslation('inventoryList');

    const { permissions: accountPermissions } = useAccount();

    const translate = useTranslatedString();

    const searchBox = useSearchInDropDown();

    // get company context
    const company = useCompany(true);

    const { dealerIds } = useMultipleDealerIds();

    const [currentCache, setCache] = useSortAndFilterCache(SortAndFilterCacheKey.MobilityBooking);

    // get state from a reducer
    const [state, dispatch] = useListReducer(currentCache);

    const navigate = useNavigate();

    // fetch data
    const { page, pageSize, sort, filter } = state;

    const { data, loading, error } = useListInventoriesQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            pagination: { offset: (page - 1) * pageSize, limit: pageSize },
            sort: pick(['field', 'order'], sort),
            filter: { ...filter, companyId: company?.id, dealerIds, configuratorId: modelConfiguratorId },
        },
    });

    const onChange = useCallback(
        (pagination, filters, sorter, extra) => {
            switch (extra.action) {
                case 'sort':
                    return dispatch({
                        type: 'setSort',
                        sortBy: getSortField(sorter),
                    });

                case 'filter':
                    return dispatch({
                        type: 'setFilter',
                        filterBy: {
                            modelName: !filters.modelName ? null : filters.modelName[0],
                            subModelName: !filters.subModelName ? null : filters.subModelName[0],
                            variantName: !filters.variantName ? null : filters.variantName[0],
                            colorSettingName: !filters.colorSettingName ? null : filters.colorSettingName[0],
                            trimSettingName: !filters.trimSettingName ? null : filters.trimSettingName[0],
                            packageSettingName: !filters.packageSettingName ? null : filters.packageSettingName[0],
                            vin: !filters.vin ? null : filters.vin[0],
                        },
                    });

                default:
                    return undefined;
            }
        },
        [dispatch]
    );

    // prepare items as a data source
    const dataSource = useMemo(() => data?.list?.items || [], [data]);

    const total = data?.list?.count || 0;

    const onRow = useCallback(
        (row: InventoryDetailsDataFragment) => {
            setCache(state);
            navigate(`inventory/${row.id}`, {
                state: { tabs: activeTab, modelConfiguratorId, currentPage: 'modelConfigurator' },
            });
        },
        [activeTab, modelConfiguratorId, navigate, setCache, state]
    );

    const {
        modelNameSort,
        subModelSort,
        variantNameSort,
        configuratorColorNameSort,
        configuratorTrimNameSort,
        configuratorPackageNameSort,
        assignedVinCountSort,
    } = useSortOrder(sort);

    if (!loading && error) {
        return <InternalErrorResult />;
    }

    if (!hasPermissions(accountPermissions, [permissionKind.viewInventories])) {
        return <NotFoundResult />;
    }

    return (
        <PaginatedTableWithContext
            company={company}
            dataSource={dataSource}
            dispatch={dispatch}
            loading={loading}
            onChange={onChange}
            onRow={(row: InventoryDetailsDataFragment) => ({
                onClick: () => onRow(row),
                style: { cursor: 'pointer' },
            })}
            rowKey="id"
            scroll={{ x: true }}
            state={state}
            tableName={t('inventoryList:title')}
            total={total}
        >
            <Table.Column
                key="modelName"
                dataIndex={['model', 'name']}
                filterDropdown={searchBox.render(t('inventoryList:columns.model'))}
                filterIcon={renderSearchIcon}
                filteredValue={filter.modelName ? [filter.modelName] : null}
                onFilterDropdownVisibleChange={searchBox.autoFocus}
                render={value => translate(value)}
                sortOrder={modelNameSort}
                title={t('inventoryList:columns.model')}
                sorter
            />
            <Table.Column
                key="subModelName"
                dataIndex={['subModel', 'name']}
                filterDropdown={searchBox.render(t('inventoryList:columns.subModel'))}
                filterIcon={renderSearchIcon}
                filteredValue={filter.subModelName ? [filter.subModelName] : null}
                onFilterDropdownVisibleChange={searchBox.autoFocus}
                render={value => translate(value)}
                sortOrder={subModelSort}
                title={t('inventoryList:columns.subModel')}
                sorter
            />
            <Table.Column
                key="variantName"
                dataIndex={['variant', 'name']}
                filterDropdown={searchBox.render(t('inventoryList:columns.variant'))}
                filterIcon={renderSearchIcon}
                filteredValue={filter.variantName ? filter.variantName : null}
                onFilterDropdownVisibleChange={searchBox.autoFocus}
                render={value => translate(value)}
                sortOrder={variantNameSort}
                title={t('inventoryList:columns.variant')}
                sorter
            />
            <Table.Column
                key="colorSettingName"
                dataIndex={['colorSetting', 'name']}
                filterDropdown={searchBox.render(t('inventoryList:columns.color'))}
                filterIcon={renderSearchIcon}
                filteredValue={filter.colorSettingName ? filter.colorSettingName : null}
                onFilterDropdownVisibleChange={searchBox.autoFocus}
                sortOrder={configuratorColorNameSort}
                title={t('inventoryList:columns.color')}
                sorter
            />
            <Table.Column
                key="trimSettingName"
                dataIndex={['trimSetting', 'name']}
                filterDropdown={searchBox.render(t('inventoryList:columns.trim'))}
                filterIcon={renderSearchIcon}
                filteredValue={filter.trimSettingName ? filter.trimSettingName : null}
                onFilterDropdownVisibleChange={searchBox.autoFocus}
                sortOrder={configuratorTrimNameSort}
                title={t('inventoryList:columns.trim')}
                sorter
            />
            <Table.Column
                key="packageSettingName"
                dataIndex={['packageSetting', 'packageName']}
                filterDropdown={searchBox.render(t('inventoryList:columns.package'))}
                filterIcon={renderSearchIcon}
                filteredValue={filter.packageSettingName ? filter.packageSettingName : null}
                onFilterDropdownVisibleChange={searchBox.autoFocus}
                render={value => value ?? '-'}
                sortOrder={configuratorPackageNameSort}
                title={t('inventoryList:columns.package')}
                sorter
            />
            <Table.Column
                key="assignedVinCount"
                dataIndex="assignedVinCount"
                render={value => value ?? '-'}
                sortOrder={assignedVinCountSort}
                // for sake of sort
                title={t('inventoryList:columns.vin')}
                sorter
            />
            <Table.Column
                key="stock"
                dataIndex="stocks"
                render={(stocks: StockInventorySpecsFragment[], record: InventoryListDataFragment) => (
                    <span>
                        {record.remainingStock} ({record.reservedStock}) / {stocks.length}
                    </span>
                )}
                title={
                    <Space>
                        <span>{t('inventoryList:columns.stock')}</span>
                        <Tooltip title={t('inventoryList:columns.stockTooltip')}>
                            <InfoCircleOutlined />
                        </Tooltip>
                    </Space>
                }
            />
            <Table.Column
                key="isActive"
                align="center"
                dataIndex="isActive"
                render={value => renderBooleanIcon(value, true)}
                title={t('inventoryList:columns.isActive')}
                width={120}
            />
        </PaginatedTableWithContext>
    );
};

export default InventoryConfiguratorList;
